/** 판매 채널과 채널 규격.
 *
 * **왜 이 파일이 지금 생겼는가.** 1단계는 네이버 스마트스토어 하나만 판다. 그런데 `860`,
 * `1000×1000`, 블록 다섯 종, 세로 5,000px 상한은 전부 **네이버가 정한 값**이지 이 제품이 정한
 * 값이 아니다. 그것들이 `docs/TEMPLATE.md` 와 코드에 전역 상수로 박혀 있으면, 채널이 하나
 * 늘 때 값을 전수로 찾아 고쳐야 하고 그 과정에서 반드시 한 자리가 남는다.
 *
 * 그래서 값을 상수가 아니라 **채널 규격 레코드**로 둔다. 지금 레코드가 하나뿐이어도 형태가
 * 이미 여럿을 담는 모양이면, 두 번째 채널은 레코드 추가로 끝난다.
 *
 * **정본은 여전히 `docs/TEMPLATE.md` 다.** 이 파일은 `domain.ts` 가 D1 의 CHECK 문자열을
 * 옮겨 적는 것과 같은 관계로 그 문서를 옮겨 적는다. 값을 바꾸려면 문서를 먼저 고친다.
 *
 * **저장은 채널 중립, 렌더는 채널 규격.** 셀러가 올린 사진과 배경을 지운 결과는 채널과
 * 무관하다. 채널이 정하는 것은 캔버스 규격·블록 구성·파일 이름·내보내기 형식뿐이다.
 * 그래서 채널은 작업이 아니라 **내보낸 판**에 붙는다(`docs/DATA_DICTIONARY.md` 6장).
 */

/** 셀러가 상세페이지를 올리는 곳.
 *
 * 값이 지금 하나뿐이어도 타입으로 존재해야 한다. 나중에 더하면 그때까지 쌓인 판에 채널이
 * 없어 어느 규격으로 만든 파일인지 소급할 수 없다.
 *
 * ⚠️ 값을 늘리면 `CHANNEL_SPECS` 에도 레코드를 더해야 한다. `Record<SalesChannel, ...>` 라
 * 빠뜨리면 컴파일에서 막힌다. 그게 이 형태를 고른 이유다. */
export type SalesChannel = "SMARTSTORE";

/** 지금 실제로 내보낼 수 있는 채널. 화면의 채널 고르기가 이 목록을 읽는다. */
export const SUPPORTED_SALES_CHANNELS: readonly SalesChannel[] = ["SMARTSTORE"] as const;

/** 1단계의 유일한 채널. 채널을 안 받는 옛 경로가 쓰는 기본값이다. */
export const DEFAULT_SALES_CHANNEL: SalesChannel = "SMARTSTORE";

/** 채널 규격이 바뀐 판 번호. **판마다 이 값을 함께 저장해야 한다.**
 *
 * 네이버가 권장 폭을 860 에서 다른 값으로 바꾸면 새 판부터 그 값으로 렌더된다. 그때 옛 판의
 * `다시 받기`(목업 `W-50`)가 새 규격 숫자를 말하면 셀러는 받은 파일과 화면이 다른 것을 본다.
 * 렌더가 끝난 뒤에는 어느 규격으로 만들었는지 되찾을 방법이 없으므로 그 순간에 기록해야 한다. */
export type ChannelSpecVersion = string;

/** 0003 이전에 쌓인 판의 규격 판. **"스마트스토어 규격이었을 것"이라는 추정이지 사실이
 *  아니다.** 마이그레이션이 이 값으로 백필했고(`0003_channel_dimension.sql`), 화면은 이
 *  값을 크기 표기의 근거로 쓰지 않는다. 렌더 시점의 규격을 모르기 때문이다. */
export const LEGACY_CHANNEL_SPEC_VERSION: ChannelSpecVersion = "naver-legacy";

export interface ChannelBlockSpec {
  /** 블록 종류. 정본은 `docs/TEMPLATE.md` 3장. */
  blockType: string;
  /** 파일 이름의 `{블록타입}` 자리에 들어가는 말. 블록 종류와 다를 수 있다
   *  (`detail-crop` 블록의 파일 이름은 `crop` 이다. 목업 `W-40`). */
  fileNameToken: string;
  width: number;
  /** 내용에 따라 늘어나는 블록은 `null`. 스펙표·유의사항이 그렇다. */
  height: number | null;
  /** 사진 한 장이 블록 하나가 되는가. `gallery`·`detail-crop` 이 그렇다. */
  repeatsPerImage: boolean;
}

export interface ChannelSpec {
  channel: SalesChannel;
  /** 이 규격의 판. 판을 만들 때 함께 저장한다. */
  specVersion: ChannelSpecVersion;
  /** 셀러에게 보여줄 이름. 화면 문구라 구현 용어를 쓰지 않는다. */
  displayName: string;

  /** 상세 이미지 가로. 스마트스토어는 860. */
  canvasWidth: number;
  /** 이미지 한 장의 세로 상한. 이것 때문에 블록을 한 장으로 잇지 않는다. */
  maxImageHeight: number;
  /** 이미지 한 장의 용량 상한(바이트). */
  maxImageBytes: number;

  /** 대표이미지. 정사각이라 한 변만 둔다. */
  thumbnailSize: number;
  thumbnailFileName: string;

  /** 상세 이미지 파일 이름의 앞머리. `detail-01-hero.jpg` 의 `detail`. */
  detailFileNamePrefix: string;
  /** 내보내는 파일 형식. */
  exportContentType: string;
  /** 여러 파일을 한 번에 받을 때의 묶음. 채널이 다른 형식을 요구하면 여기가 갈린다. */
  bundleFormat: "ZIP";

  /** 이 채널이 쓰는 블록과 그 크기. **채널마다 다르다**. 켜고 끄기의 대상도 이 목록이다. */
  blocks: readonly ChannelBlockSpec[];

  /** 상품명 길이 상한. 채널마다 다르고, 내보낼 때 검사한다. `null` = 상한 미확인. */
  productNameMaxLength: number | null;
}

/** 네이버 스마트스토어 규격. 값의 출처는 `docs/TEMPLATE.md` 1·2·3장이다.
 *
 * `notice` 는 배송·교환 문구라 사진과 무관하고 셀러 설정에서 온다. `spec` 과 함께 세로가
 * 내용에 따라 늘어나므로 `height` 가 `null` 이다. */
export const SMARTSTORE_SPEC: ChannelSpec = {
  channel: "SMARTSTORE",
  specVersion: "naver-2026-09",
  displayName: "스마트스토어",

  canvasWidth: 860,
  maxImageHeight: 5000,
  maxImageBytes: 20 * 1024 * 1024,

  thumbnailSize: 1000,
  thumbnailFileName: "thumbnail.jpg",

  detailFileNamePrefix: "detail",
  exportContentType: "image/jpeg",
  bundleFormat: "ZIP",

  blocks: [
    { blockType: "hero", fileNameToken: "hero", width: 860, height: 860, repeatsPerImage: false },
    {
      blockType: "gallery",
      fileNameToken: "gallery",
      width: 860,
      height: 860,
      repeatsPerImage: true,
    },
    {
      blockType: "detail-crop",
      fileNameToken: "crop",
      width: 860,
      height: 430,
      repeatsPerImage: true,
    },
    { blockType: "spec", fileNameToken: "spec", width: 860, height: null, repeatsPerImage: false },
    {
      blockType: "notice",
      fileNameToken: "notice",
      width: 860,
      height: null,
      repeatsPerImage: false,
    },
  ],

  // 스마트스토어 상품명 상한은 `docs/NAVER_API_CATALOG.md` 확인 전이라 열어 둔다.
  productNameMaxLength: null,
};

/** 채널 → 규격. **채널을 더하면 여기 레코드를 더한다.** 빠뜨리면 컴파일에서 막힌다. */
export const CHANNEL_SPECS: Record<SalesChannel, ChannelSpec> = {
  SMARTSTORE: SMARTSTORE_SPEC,
};

export function channelSpec(channel: SalesChannel = DEFAULT_SALES_CHANNEL): ChannelSpec {
  return CHANNEL_SPECS[channel];
}

/** 그 채널이 쓰는 블록인가. 셀러가 끈 블록과 **채널에 애초에 없는 블록**은 다른 것이다.
 *  전자는 켤 수 있고 후자는 켤 자리가 없다. 화면이 둘을 같이 그리면 셀러가 켜 보고
 *  아무 일도 안 일어나는 것을 겪는다. */
export function channelHasBlock(channel: SalesChannel, blockType: string): boolean {
  return channelSpec(channel).blocks.some((b) => b.blockType === blockType);
}

export function channelBlockSpec(
  channel: SalesChannel,
  blockType: string,
): ChannelBlockSpec | null {
  return channelSpec(channel).blocks.find((b) => b.blockType === blockType) ?? null;
}

/** 업로드 순번이 배정받는 블록. 정본은 `docs/TEMPLATE.md` 5장 — 1번째 `hero`, 2~n `gallery`.
 *  `detail-crop` 은 셀러가 크롭을 지정해야 생기므로 순번만으로는 나오지 않는다.
 *
 * **`sortOrder` 는 0부터다**(`JobImage.sortOrder`. 0번이 대표컷). `imageRoleLabel` 과 **같은
 * 판정축을 쓴다** — 한쪽이 `대표컷`이라 말하는 장에 다른 쪽이 `gallery` 를 배정하면 셀러가
 * 보는 이름과 만들어지는 파일이 갈린다.
 *
 * **채널 규격에서 뽑는다.** `hero`·`gallery` 를 여기 적어 두면 두 번째 판매처가 붙는 날
 * 이 함수와 `CHANNEL_SPECS` 두 곳을 맞춰야 한다. 규격에서 읽는 규칙은 두 줄이다:
 * 대표컷은 **블록 목록의 첫 칸**(렌더 순서대로 적혀 있다), 나머지는 **처음 나오는
 * `repeatsPerImage` 블록**(사진 한 장이 블록 하나가 되는 자리). `spec`·`notice` 는 사진을
 * 받지 않는데 `repeatsPerImage` 가 `false` 라는 점만으로는 `hero` 와 갈리지 않아, 첫 칸이라는
 * 위치로 가른다. **채널을 더할 때 블록 목록을 렌더 순서로 적어야 이 규칙이 선다.**
 *
 * ⚠️ 반환값은 블록 종류이지 파일 이름 토큰이 아니다. 파일 이름은 `detailFileName` 이 만든다
 * (`detail-crop` 의 토큰이 `crop` 인 것처럼 둘은 다를 수 있다). */
export function blockTypeForUploadOrder(channel: SalesChannel, sortOrder: number): string {
  const blocks = channelSpec(channel).blocks;
  const lead = blocks[0];
  const perImage = blocks.find((b) => b.repeatsPerImage);
  // 사진을 받을 블록이 하나도 없는 규격은 애초에 아무것도 렌더할 수 없다. 조용히 빈
  // 문자열을 돌려주면 그 값이 파일 이름과 `JobVersionFile.blockType` 까지 흘러간다.
  if (!lead && !perImage) {
    throw new Error(`채널 규격에 블록이 없다: ${channel} (CHANNEL_SPECS 를 확인할 것)`);
  }
  // `imageRoleLabel` 과 같은 판정: 0번만 대표컷이다.
  if (sortOrder === 0) return (lead ?? perImage)!.blockType;
  return (perImage ?? lead)!.blockType;
}

/** 내보낸 파일 이름. **채널마다 규칙이 다르므로 문자열을 화면에서 조립하지 않는다.**
 *  `sequence` 는 1부터. `detail-01-hero.jpg`(목업 `W-40`·`A-52`). */
export function detailFileName(
  channel: SalesChannel,
  sequence: number,
  blockType: string,
  extension = "jpg",
): string {
  const spec = channelSpec(channel);
  const token = channelBlockSpec(channel, blockType)?.fileNameToken ?? blockType;
  const padded = sequence < 10 ? `0${sequence}` : `${sequence}`;
  return `${spec.detailFileNamePrefix}-${padded}-${token}.${extension}`;
}

/** 내보낼 파일 하나. */
export interface ExportFile {
  name: string;
  /** `1000×1000` 처럼 화면에 그대로 쓰는 문자열. 가변 높이는 `860×가변`. */
  size: string;
}

/** 만들어진 장수로 내보낼 파일 목록을 만든다. 1번째가 hero, 나머지가 gallery 다.
 *
 * **파일 이름도 크기도 판매처 규격에서 온다.** 문자열을 화면에서 조립하지 않는다.
 *
 * ⭐ **이 함수는 앱(`apps/mobile/src/lib/template.ts`)에서 이리로 올라왔다**(PRD `13.1`
 * D-93). 못 만든 장이 섞인 내보내기(`export-decline.ts`)가 받는 파일 수를 이 목록의 길이로
 * 세는데, 그 판정을 앱과 웹이 함께 읽으려면 파일 수도 한 자리에서 나와야 한다.
 * ⛔ **웹에서 파일 수를 다시 세지 않는다.** 세면 같은 판의 파일 수가 기기마다 갈린다. */
export function exportFileList(
  doneImageCount: number,
  channel: SalesChannel = DEFAULT_SALES_CHANNEL,
): ExportFile[] {
  if (doneImageCount <= 0) return [];
  const spec = channelSpec(channel);
  const size = (blockType: string) => {
    const block = spec.blocks.find((item) => item.blockType === blockType);
    if (!block) return `${spec.canvasWidth}×가변`;
    return `${block.width}×${block.height ?? "가변"}`;
  };

  const files: ExportFile[] = [
    { name: spec.thumbnailFileName, size: `${spec.thumbnailSize}×${spec.thumbnailSize}` },
    { name: detailFileName(channel, 1, "hero"), size: size("hero") },
  ];
  for (let i = 1; i < doneImageCount; i += 1) {
    files.push({
      name: detailFileName(channel, i + 1, "gallery"),
      size: size("gallery"),
    });
  }
  return files;
}
